/**
 * Represents a value object for uniquely identifying an entity.
 *
 * This interface provides a standardized way to define, store, and access
 * unique identifiers. It ensures type safety by allowing the raw value of the
 * identifier to be strongly typed.
 *
 * @typeParam T - The type of the raw value used as the identifier (e.g., string, number, etc.).
 */
export interface Identifier< T > {
    rawValue: T
}

export type AllNullable<T> = { [ K in keyof T]: T[ K ] | null }

/**
 * creates an object of type Identifier< number > from given number
 * @param id
 */
export function makeNumericId(id: number ): Identifier<number> {
    return { rawValue: id };
}

/**
 * Generates a string-based identifier object.
 *
 * This function creates an identifier object with a `rawValue` property derived from the provided string.
 * It is used to ensure consistent handling of string-based identifiers across the application.
 *
 * @param id The raw string input to be wrapped as an identifier.
 * @return An identifier object containing the input string as its `rawValue` property.
 */
export function makeStringId( id: string ): Identifier<string> {
    return { rawValue: id };
}

/**
 * type guard to check if the prop is of type Identifier<number>
 *
 * @param prop
 */
export function isNumberIdentifier( prop: any ): prop is Identifier<number> {
    return ( prop as Identifier<number> ).rawValue !== undefined
}

/**
 * getNumberId checks what type of id the parameter is and returns the id
 *
 * there are three type possibilities
 *
 * a) param obj is itself of number and will be returned immediately
 * b) param obj is of type Identifier< number >, in this case rawValue property will be returned
 * c) param obj is not of type Identifier<number> nor of type number, an error will be thrown to inform the developer early
 *
 * @param obj
 */
export function getNumberId( obj: any ): number {
    if( typeof obj === 'number') {
        return obj;
    }

    if( isNumberIdentifier( obj ) ) {
        return obj.rawValue;
    } else {
        throw Error( "Not correctly formed id type! Identifiers must be of type number or Identifier< number >" );
    }
}